import React, { useState, useEffect, useMemo } from "react";
import Breadcrumbs from "~/components/Breadcrumbs";
import PageHeader from "~/components/PageHeader";
import { getCurrentPage } from "~/functions";
import api from "~/services/api";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Search, ChevronLeft, ChevronRight, X, ArrowUpDown } from "lucide-react";

export default function TimesheetCreate() {
  const currentPage = getCurrentPage();
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = id && id !== "new";

  const handleBackToList = () => {
    navigate("/Administrative/HR/Timesheet");
  };

  const [entries, setEntries] = useState([]);

  const [viewMode, setViewMode] = useState("table");
  const [periodFrom, setPeriodFrom] = useState("2025-01-06");
  const [periodTo, setPeriodTo] = useState("2025-01-19");
  const [adjustments, setAdjustments] = useState([
    { id: 1, description: "", hours: 0, employee_movement_id: null, entry_type: "adjustment" },
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTimesheetId, setCurrentTimesheetId] = useState(null);
  const [status, setStatus] = useState("draft");
  const [staffs, setStaffs] = useState([]);
  const [movementOptions, setMovementOptions] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [selectedStaffName, setSelectedStaffName] = useState("");
  const [selectedStaffType, setSelectedStaffType] = useState("");
  const [entriesLoadedFromAPI, setEntriesLoadedFromAPI] = useState(false);
  const [expectedHours, setExpectedHours] = useState(0);
  const [conflictInfo, setConflictInfo] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [filterEmployeeType, setFilterEmployeeType] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const createTempId = () =>
    `temp-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const getFortnightEnd = (from) => {
    if (!from) return null;
    const start = new Date(from + "T00:00:00");
    if (Number.isNaN(start.getTime())) return null;
    const end = new Date(start);
    end.setDate(end.getDate() + 14);
    return end.toISOString().split("T")[0];
  };

  const calculateWeeksBetweenDates = (from, to) => {
    if (!from || !to) return 1;
    const start = new Date(from + "T00:00:00");
    const end = new Date(to + "T00:00:00");
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;
    const diffDays = Math.floor((end - start) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 1;
    return Math.max(1, Math.ceil(diffDays / 7));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    return {
      weekday: date.toLocaleDateString("en-US", { weekday: "short" }),
      day: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    };
  };

  const calculateHours = (from, to) => {
    if (!from || !to) return 0;
    const [fromH, fromM] = from.split(":").map(Number);
    const [toH, toM] = to.split(":").map(Number);
    return ((toH * 60 + toM) - (fromH * 60 + fromM)) / 60;
  };

  const normalizeTimeValue = (value) => {
    if (!value) return "";
    if (typeof value !== "string") return "";
    return value.slice(0, 5);
  };

  const calculateShiftHours = (entry) => {
    const shiftPairs = [
      [entry.morning_from, entry.morning_to],
      [entry.afternoon_from, entry.afternoon_to],
      [entry.evening_from, entry.evening_to],
    ];
    return shiftPairs.reduce(
      (sum, [from, to]) => sum + calculateHours(from, to),
      0
    );
  };

  const isAutoEightMovement = (movementName = "") => {
    const normalized = movementName.trim().toLowerCase();
    return (
      normalized === "sick day" ||
      normalized === "sickday" ||
      normalized === "vacation"
    );
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm((searchTerm || "").trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedTerm, filterEmployeeType, filterDepartment]);

  const filteredStaffs = useMemo(() => {
    const term = debouncedTerm;
    let filtered = staffs.filter((staff) => {
      const matchSearch = !term || staff.name.toLowerCase().includes(term);
      const matchType = !filterEmployeeType || staff.employee_type === filterEmployeeType;
      const matchDepartment = !filterDepartment || (staff.department?.name || "").toLowerCase().includes(filterDepartment.toLowerCase());
      return matchSearch && matchType && matchDepartment;
    });

    filtered.sort((a, b) => {
      let aVal, bVal;
      if (sortBy === "name") {
        aVal = a.name || "";
        bVal = b.name || "";
      } else if (sortBy === "employee_type") {
        aVal = a.employee_type || "";
        bVal = b.employee_type || "";
      } else if (sortBy === "department") {
        aVal = a.department?.name || "";
        bVal = b.department?.name || "";
      }

      if (sortOrder === "asc") {
        return aVal.localeCompare(bVal);
      } else {
        return bVal.localeCompare(aVal);
      }
    });

    return filtered;
  }, [staffs, debouncedTerm, filterEmployeeType, filterDepartment, sortBy, sortOrder]);

  const totalPages = Math.max(1, Math.ceil((filteredStaffs?.length || 0) / PAGE_SIZE));
  const pageStaffs = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredStaffs.slice(start, start + PAGE_SIZE);
  }, [filteredStaffs, page]);

  const handleSelectStaff = (staff) => {
    setSelectedStaffId(staff.id);
    setSelectedStaffName(staff.name);
    setSelectedStaffType(staff.employee_type || "");
    setIsStaffModalOpen(false);
    setSearchTerm("");
    setFilterEmployeeType("");
    setFilterDepartment("");
    setPage(1);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const generateEntriesForPeriod = (from, to) => {
    const entries = [];
    const startDate = new Date(from + "T00:00:00");
    const endDate = new Date(to + "T00:00:00");

    
    const regularOption = movementOptions.find(
      (opt) => opt.name.toLowerCase() === "regular"
    );

    let currentDate = new Date(startDate);
    let id = 1;

    while (currentDate <= endDate) {
      entries.push({
        id: id++,
        date: currentDate.toISOString().split("T")[0],
        from: "",
        to: "",
        morning_from: "",
        morning_to: "",
        afternoon_from: "",
        afternoon_to: "",
        evening_from: "",
        evening_to: "",
        description: "",
        hours: 0,
        employee_movement_id: regularOption?.id || null,
        entry_type: "work",
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return entries;
  };

  useEffect(() => {
    async function loadTimesheetData() {
      if (!isEditMode) return;

      setIsLoading(true);
      try {
        const { data } = await api.get(`/timesheets/${id}`);
        console.log("Timesheet data loaded:", data);

        setCurrentTimesheetId(data.id);
        setSelectedStaffId(data.staff_id);
        setSelectedStaffName(data.staff?.name || "");
        setSelectedStaffType(data.staff?.employee_type || "");
        setPeriodFrom(data.period_from);
        setPeriodTo(data.period_to);
        setStatus(data.status);

        const apiEntries = data.entries || [];
        const formattedEntries = [];
        const formattedAdjustments = [];

        apiEntries.forEach((entry, index) => {
          const base = {
            id: entry.id || index + 1,
            description: entry.description || "",
            employee_movement_id: entry.employee_movement_id || null,
            hours: entry.hours || 0,
            entry_type: entry.entry_type || "work",
          };
          if ((entry.entry_type || "work") === "adjustment") {
            formattedAdjustments.push({
              ...base,
              date: entry.date || periodFrom || null,
              from: null,
              to: null,
            });
          } else {
            const formatted = {
              ...base,
              date: entry.date,
              from: entry.time_from ? normalizeTimeValue(entry.time_from) : "",
              to: entry.time_to ? normalizeTimeValue(entry.time_to) : "",
              morning_from: normalizeTimeValue(entry.morning_from),
              morning_to: normalizeTimeValue(entry.morning_to),
              afternoon_from: normalizeTimeValue(entry.afternoon_from),
              afternoon_to: normalizeTimeValue(entry.afternoon_to),
              evening_from: normalizeTimeValue(entry.evening_from),
              evening_to: normalizeTimeValue(entry.evening_to),
            };
            formattedEntries.push({
              ...formatted,
              hours: entry.hours || calculateShiftHours(formatted),
            });
          }
        });

        setEntriesLoadedFromAPI(true);
        setEntries(formattedEntries);
        setAdjustments(
          formattedAdjustments.length > 0
            ? formattedAdjustments
            : [{ id: 1, description: "", hours: 0, employee_movement_id: null, entry_type: "adjustment" }]
        );
        toast.success("Timesheet loaded successfully");
      } catch (err) {
        console.error("Error loading timesheet:", err);
        toast.error(err?.response?.data?.error || "Failed to load timesheet");
      }
      setIsLoading(false);
    }

    loadTimesheetData();
  }, [id, isEditMode]);

  useEffect(() => {
    async function loadStaffs() {
      try {
        const { data } = await api.get("/staffs-simple");
        setStaffs(data || []);
        console.log("Staffs loaded:", data);
      } catch (err) {
        console.error("Error loading staffs:", err);
        toast.error("Failed to load staffs list");
      }
    }
    loadStaffs();
  }, []);

  useEffect(() => {
    if (!selectedStaffId || selectedStaffType) {
      return;
    }
    const staff = staffs.find(
      (item) => String(item.id) === String(selectedStaffId)
    );
    if (staff?.employee_type) {
      setSelectedStaffType(staff.employee_type);
    }
  }, [selectedStaffId, selectedStaffType, staffs]);

  useEffect(() => {
    async function loadMovements() {
      try {
        const { data } = await api.get("/employeemovements", {
          params: { limit: 999, page: 1, orderBy: "name", orderASC: "ASC" },
        });
        const options = (data?.rows || data || []).map((item) => ({
          id: item.id,
          name: item.name,
          affects_balance: item.affects_balance,
        }));
        setMovementOptions(options);

       
        if (!isEditMode && !entriesLoadedFromAPI) {
          const regularOption = options.find(
            (opt) => opt.name.toLowerCase() === "regular"
          );
          if (regularOption) {
            console.log("Auto-selecting Regular type:", regularOption.id);
          
            setEntries((prevEntries) =>
              prevEntries.map((entry) => ({
                ...entry,
                employee_movement_id: regularOption.id,
              }))
            );
          }
        }
      } catch (err) {
        console.error("Error loading employee movements:", err);
        toast.error("Failed to load movement types");
      }
    }
    loadMovements();
  }, [isEditMode, entriesLoadedFromAPI]);

  useEffect(() => {
    if (!selectedStaffId) {
      setExpectedHours(0);
      return;
    }

    const staff = staffs.find(
      (item) => String(item.id) === String(selectedStaffId)
    );
    const hours = staff?.hours_per_week;
    const parsedHours = parseFloat(hours);
    if (!Number.isFinite(parsedHours)) {
      setExpectedHours(0);
      return;
    }
    const weeks = calculateWeeksBetweenDates(periodFrom, periodTo);
    setExpectedHours(parsedHours * weeks);
  }, [selectedStaffId, staffs, periodFrom, periodTo]);

  useEffect(() => {
    if (periodFrom && periodTo && !isEditMode && !isLoading && !entriesLoadedFromAPI) {
      const newEntries = generateEntriesForPeriod(periodFrom, periodTo);
      setEntries(newEntries);
    }
  }, [periodFrom, periodTo, isEditMode, isLoading, entriesLoadedFromAPI]);

  const handleConfirmOverride = () => {
    if (!conflictInfo?.allowOverride) {
      setShowConflictModal(false);
      return;
    }
    const overrideStatus = conflictInfo?.newTimesheetStatusOnOverride || status;
    setShowConflictModal(false);
    setStatus(overrideStatus);
    handleSubmitTimesheet({
      forceOverride: true,
      newStatusOnOverride: conflictInfo?.newTimesheetStatusOnOverride,
    });
  };

  const handleAddEntry = () => {
    const lastEntry = entries[entries.length - 1];
    if (!lastEntry) return;

    const lastDate = new Date(lastEntry.date + "T00:00:00");
    lastDate.setDate(lastDate.getDate() + 1);
    const newDate = lastDate.toISOString().split('T')[0];

   
    const regularOption = movementOptions.find(
      (opt) => opt.name.toLowerCase() === "regular"
    );

    const newId = createTempId();
    setEntries([
      ...entries,
      {
        id: newId,
        date: newDate,
        from: "",
        to: "",
        morning_from: "",
        morning_to: "",
        afternoon_from: "",
        afternoon_to: "",
        evening_from: "",
        evening_to: "",
        description: "",
        hours: 0,
        employee_movement_id: regularOption?.id || null,
        entry_type: "work",
      },
    ]);

    setPeriodTo(newDate);
  };

  const handleAddAdjustment = () => {
    const newId = createTempId();
    setAdjustments([
      ...adjustments,
      { id: newId, description: "", hours: 0, employee_movement_id: null, entry_type: "adjustment" },
    ]);
  };

  const handleAdjustmentChange = (id, field, value) => {
    setAdjustments(
      adjustments.map((adj) => {
        if (adj.id === id) {
          const updated = {
            ...adj,
            [field]:
              field === "hours" ? parseFloat(value || 0) : value || null,
          };
          if (field === "employee_movement_id") {
            const selectedMovement = movementOptions.find(
              (m) => String(m.id) === String(value)
            );
            if (selectedMovement?.affects_balance === "non-paid") {
              updated.hours = 0;
            }
          }
          return updated;
        }
        return adj;
      })
    );
  };

  const handleDeleteAdjustment = (id) => {
    setAdjustments(adjustments.filter((adj) => adj.id !== id));
  };

  const handleDeleteEntry = (id) => {
    const filtered = entries.filter(e => e.id !== id);
    setEntries(filtered);

    if (filtered.length > 0) {
      const dates = filtered.map(e => e.date).sort();
      setPeriodFrom(dates[0]);
      setPeriodTo(dates[dates.length - 1]);
    }
  };

  const handleFieldChange = (id, field, value) => {
    setEntries(entries.map((e) => {
      if (e.id === id) {
        const updated = { ...e, [field]: value };
        const isShiftField =
          field === "morning_from" ||
          field === "morning_to" ||
          field === "afternoon_from" ||
          field === "afternoon_to" ||
          field === "evening_from" ||
          field === "evening_to";
        if (field === "from" || field === "to") {
          updated.hours = calculateHours(updated.from, updated.to);
        }
        if (isShiftField) {
          updated.hours = calculateShiftHours(updated);
        }
        if (field === "employee_movement_id") {
          const selectedMovement = movementOptions.find(
            (m) => String(m.id) === String(value)
          );
          if (selectedMovement && isAutoEightMovement(selectedMovement.name)) {
            updated.hours = 8;
          }
          if (selectedMovement?.affects_balance === "non-paid") {
            updated.hours = 0;
            updated.from = "";
            updated.to = "";
            updated.morning_from = "";
            updated.morning_to = "";
            updated.afternoon_from = "";
            updated.afternoon_to = "";
            updated.evening_from = "";
            updated.evening_to = "";
          }
        }
        return updated;
      }
      return e;
    }));
  };

  const handleGetTimesheetForUser = async () => {
    setIsLoading(true);
    try {
      if (!selectedStaffId) {
        toast.error("Please select a staff member first.");
        setIsLoading(false);
        return;
      }
      if (!periodFrom || !periodTo) {
        toast.error("Please select period from and to dates.");
        setIsLoading(false);
        return;
      }

      console.log("=== GET TIMESHEET FOR USER ===");
      console.log("Staff ID:", selectedStaffId);
      console.log("Period From:", periodFrom);
      console.log("Period To:", periodTo);
      console.log("Status:", status);

      const { data } = await api.get(
        `/get-timesheet-staff/${selectedStaffId}`,
        {
          params: {
            from: periodFrom,
            to: periodTo,
            status: status,
          },
        }
      );

      console.log("Timesheet received:", data);

      setCurrentTimesheetId(null);
      setPeriodFrom(data.period_from);
      setPeriodTo(data.period_to);
      setStatus(data.status);
      const selectedStaff = staffs.find(
        (item) => String(item.id) === String(selectedStaffId)
      );
      if (selectedStaff?.employee_type) {
        setSelectedStaffType(selectedStaff.employee_type);
      }

      const apiEntries = data.entries || [];
      const formattedEntries = [];
      const formattedAdjustments = [];

      apiEntries.forEach((entry, index) => {
        const base = {
          id:
            entry.id === null || entry.id?.startsWith("temp-")
              ? index + 1
              : entry.id,
          description: entry.description || "",
          employee_movement_id: entry.employee_movement_id || null,
          hours: entry.hours || 0,
          entry_type: entry.entry_type || "work",
        };
        if ((entry.entry_type || "work") === "adjustment") {
          formattedAdjustments.push({
            ...base,
            date: entry.date || periodFrom || null,
            from: null,
            to: null,
          });
        } else {
          const formatted = {
            ...base,
            date: entry.date,
            from: entry.time_from ? normalizeTimeValue(entry.time_from) : "",
            to: entry.time_to ? normalizeTimeValue(entry.time_to) : "",
            morning_from: normalizeTimeValue(entry.morning_from),
            morning_to: normalizeTimeValue(entry.morning_to),
            afternoon_from: normalizeTimeValue(entry.afternoon_from),
            afternoon_to: normalizeTimeValue(entry.afternoon_to),
            evening_from: normalizeTimeValue(entry.evening_from),
            evening_to: normalizeTimeValue(entry.evening_to),
          };
          formattedEntries.push({
            ...formatted,
            hours: entry.hours || calculateShiftHours(formatted),
          });
        }
      });

      console.log("Formatted entries to set:", formattedEntries);

      setEntriesLoadedFromAPI(true);
      setEntries(formattedEntries);
      setAdjustments(
        formattedAdjustments.length > 0
          ? formattedAdjustments
          : [{ id: 1, description: "", hours: 0, employee_movement_id: null, entry_type: "adjustment" }]
      );

      toast.success(`Timesheet loaded with ${formattedEntries.length} entries from staff schedule!`);
    } catch (err) {
      console.error("Error getting timesheet for user:", err);
      if (err?.response?.data?.error) {
        toast.error(err.response.data.error);
      } else {
        toast.error("Failed to get timesheet for user");
      }
    }
    setIsLoading(false);
  };

  const handleSubmitTimesheet = async (options = {}) => {
    const { forceOverride = false, newStatusOnOverride = null } = options;
    setIsSaving(true);
    try {
      if (!selectedStaffId) {
        toast.error("Please select a staff member before submitting.");
        return;
      }

      const isFaculty = selectedStaffType === "Faculty";
      const validEntries = entries.map((e) => ({
        date: e.date || null,
        time_from: isFaculty ? null : e.from ? e.from + ":00" : null,
        time_to: isFaculty ? null : e.to ? e.to + ":00" : null,
        morning_from:
          isFaculty && e.morning_from ? e.morning_from + ":00" : null,
        morning_to: isFaculty && e.morning_to ? e.morning_to + ":00" : null,
        afternoon_from:
          isFaculty && e.afternoon_from ? e.afternoon_from + ":00" : null,
        afternoon_to:
          isFaculty && e.afternoon_to ? e.afternoon_to + ":00" : null,
        evening_from:
          isFaculty && e.evening_from ? e.evening_from + ":00" : null,
        evening_to: isFaculty && e.evening_to ? e.evening_to + ":00" : null,
        description: e.description || "",
        hours: e.hours || 0,
        employee_movement_id: e.employee_movement_id || null,
        entry_type: e.entry_type || "work",
      }));

      const adjustmentEntries = adjustments
        .filter((adj) => adj.hours || adj.description || adj.employee_movement_id)
        .map((adj) => ({
          date: periodFrom || null,
          time_from: null,
          time_to: null,
          description: adj.description || "",
          hours: parseFloat(adj.hours) || 0,
          employee_movement_id: adj.employee_movement_id || null,
          entry_type: "adjustment",
        }));

      if (validEntries.length === 0 && adjustmentEntries.length === 0) {
        toast.error("Please add at least one entry before submitting.");
        return;
      }

      const payloadStatus = forceOverride && newStatusOnOverride ? newStatusOnOverride : status;

      const payload = {
        staff_id: selectedStaffId,
        period_from: periodFrom,
        period_to: periodTo,
        status: payloadStatus,
        entries: [...validEntries, ...adjustmentEntries],
        force_override: forceOverride,
      };

      console.log("=== TIMESHEET PAYLOAD ===");
      console.log("Mode:", isEditMode ? "EDIT" : "CREATE");
      console.log("Timesheet ID:", currentTimesheetId);
      console.log("Staff ID:", selectedStaffId);
      console.log("Period From:", periodFrom);
      console.log("Period To:", periodTo);
      console.log("Status:", payloadStatus);
      console.log("Total Entries:", validEntries.length);
      console.log("Entries:", validEntries);
      console.log("Complete Payload:", JSON.stringify(payload, null, 2));
      console.log("========================");

      let data;
      if (isEditMode) {
        const response = await api.put(`/timesheets/${id}`, payload);
        data = response.data;
        toast.success("Timesheet updated successfully!");
        console.log("Timesheet updated:", data);
      } else {
        const response = await api.post("/timesheets", payload);
        data = response.data;
        setCurrentTimesheetId(data.id);
        setStatus(data.status || payloadStatus);
        toast.success("Timesheet created successfully!");
        console.log("Timesheet created:", data);
      }
      setConflictInfo(null);
      setShowConflictModal(false);
    } catch (err) {
      console.error("Error submitting timesheet:", err);
      if (!isEditMode && err?.response?.status === 409) {
        const code = err?.response?.data?.code;
        if (
          code === "TIMESHEET_PERIOD_CONFLICT" ||
          code === "TIMESHEET_PERIOD_LOCKED"
        ) {
          setConflictInfo(err.response.data);
          setShowConflictModal(true);
          if (code === "TIMESHEET_PERIOD_LOCKED") {
            toast.error("Cannot override submitted/approved/rejected timesheets.");
          } else {
            toast.warn("There are existing timesheets overlapping this period.");
          }
          return;
        }
      }
      if (err?.response?.data?.error) {
        toast.error(err.response.data.error);
      } else {
        toast.error("Failed to submit timesheet");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const totalAdjustmentHours = adjustments.reduce(
    (acc, adj) => acc + (parseFloat(adj.hours) || 0),
    0
  );
  const totalHours =
    entries.reduce((acc, e) => acc + e.hours, 0) + totalAdjustmentHours;
  const isFaculty = selectedStaffType === "Faculty";

  return (
    <div className="h-full bg-white flex flex-1 flex-col justify-start items-start rounded-tr-2xl px-4 overflow-y-scroll">
      <PageHeader>
        <div className="flex items-center gap-3">
          <button
            onClick={handleBackToList}
            className="p-2 hover:bg-gray-100 rounded-sm transition-colors"
            title="Back to list"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <Breadcrumbs currentPage={currentPage} />
        </div>
      </PageHeader>

      {isLoading ? (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-mila_orange"></div>
            <p className="mt-4 text-sm text-gray-600">Loading timesheet...</p>
          </div>
        </div>
      ) : (
      <div className="w-full py-4">
        <div className="flex flex-row justify-between items-center w-full gap-2 mb-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleAddEntry}
              className="p-2 w-10 border border-mila_orange text-mila_orange transition-all hover:bg-mila_orange hover:text-white font-bold rounded-sm text-xs"
              title="Add Period Entry"
            >
              +
            </button>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">Period from / to</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={periodFrom}
                    onChange={(e) => {
                      const newFrom = e.target.value;
                      setPeriodFrom(newFrom);
                      const autoTo = getFortnightEnd(newFrom);
                      if (autoTo) {
                        setPeriodTo(autoTo);
                      }
                    }}
                    className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                  />
                  <input
                    type="date"
                    value={periodTo}
                    onChange={(e) => setPeriodTo(e.target.value)}
                    className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                  />
                </div>
              </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">Staff</label>
              <input
                type="text"
                value={selectedStaffName}
                onClick={() => !isEditMode && setIsStaffModalOpen(true)}
                readOnly
                placeholder="Click to select staff..."
                className={`px-2 py-1.5 text-xs border rounded-sm w-48 ${
                  isEditMode
                    ? "bg-gray-100 cursor-not-allowed text-gray-600"
                    : "cursor-pointer bg-white focus:ring-1 focus:ring-mila_orange"
                }`}
                disabled={isEditMode}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange w-32"
              >
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-gray-500">
                <span className="font-bold text-sky-700">{totalHours.toFixed(1)}h</span> worked of{" "}
                <span className="font-bold text-gray-700">{expectedHours}h</span> expected
                <span className={`ml-2 font-medium ${totalHours >= expectedHours ? "text-green-600" : "text-mila_orange"}`}>
                  ({totalHours >= expectedHours ? "+" : ""}{(totalHours - expectedHours).toFixed(1)}h)
                </span>
              </p>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleGetTimesheetForUser}
                disabled={isLoading || isSaving}
                className="px-4 py-2 text-xs font-semibold border border-blue-500 text-blue-600 hover:bg-blue-50 rounded-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                title="Load timesheet based on staff work schedule"
              >
                {isLoading ? "Loading..." : "Get Timesheet for User"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStatus("submitted");
                  handleSubmitTimesheet();
                }}
                disabled={isSaving || isLoading}
                className="px-4 py-2 text-xs font-semibold bg-mila_orange text-white hover:bg-orange-600 rounded-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving && status === "submitted" ? "Submitting..." : isEditMode ? "Update Timesheet" : "Submit Timesheet"}
              </button>
            </div>
          </div>
        </div>

        <div className="w-full overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-200">
                <tr>
                  <th className="px-3 py-2 text-xs font-semibold text-center border w-20">Weekday</th>
                  <th className="px-3 py-2 text-xs font-semibold text-left border w-36">Date</th>
                  {isFaculty ? (
                    <>
                      <th className="px-3 py-2 text-xs font-semibold text-center border w-32">Morning From</th>
                      <th className="px-3 py-2 text-xs font-semibold text-center border w-32">Morning To</th>
                      <th className="px-3 py-2 text-xs font-semibold text-center border w-32">Afternoon From</th>
                      <th className="px-3 py-2 text-xs font-semibold text-center border w-32">Afternoon To</th>
                      <th className="px-3 py-2 text-xs font-semibold text-center border w-32">Evening From</th>
                      <th className="px-3 py-2 text-xs font-semibold text-center border w-32">Evening To</th>
                    </>
                  ) : (
                    <>
                      <th className="px-3 py-2 text-xs font-semibold text-center border w-32">From</th>
                      <th className="px-3 py-2 text-xs font-semibold text-center border w-32">To</th>
                    </>
                  )}
                  <th className="px-3 py-2 text-xs font-semibold text-left border w-40">Type</th>
                  <th className="px-3 py-2 text-xs font-semibold text-left border">Description</th>
                  <th className="px-3 py-2 text-xs font-semibold text-center border w-24">Hours</th>
                  <th className="px-3 py-2 text-xs font-semibold text-center border w-16">Actions</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.length === 0 && (
                  <tr className="bg-blue-50 border-b-2 border-blue-200">
                    <td className="px-3 py-2 text-center border">
                      <button
                        type="button"
                        onClick={handleAddAdjustment}
                        className="p-1 w-6 h-6 border border-mila_orange text-mila_orange transition-all hover:bg-mila_orange hover:text-white font-bold rounded-sm text-xs"
                        title="Add adjustment"
                      >
                        +
                      </button>
                    </td>
                    <td
                      colSpan={isFaculty ? 11 : 7}
                      className="px-3 py-2 border text-xs text-gray-600"
                    >
                      No adjustments. Click + to add one.
                    </td>
                  </tr>
                )}
                {adjustments.map((adj) => (
                  <tr
                    key={adj.id}
                    className="bg-blue-50 border-b-2 border-blue-200"
                  >
                    <td className="px-3 py-2 text-center border">
                      <div className="flex items-center gap-2 justify-center">
                        <button
                          type="button"
                          onClick={handleAddAdjustment}
                          className="p-1 w-6 h-6 border border-mila_orange text-mila_orange transition-all hover:bg-mila_orange hover:text-white font-bold rounded-sm text-xs"
                          title="Add adjustment"
                        >
                          +
                        </button>
                        <span className="text-[10px] font-semibold text-gray-700 uppercase">
                          Adj.
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 border text-xs text-gray-600">
                      Adjustment
                    </td>
                    {isFaculty ? (
                      <>
                        <td className="px-3 py-2 border text-xs text-gray-600 text-center">
                          -
                        </td>
                        <td className="px-3 py-2 border text-xs text-gray-600 text-center">
                          -
                        </td>
                        <td className="px-3 py-2 border text-xs text-gray-600 text-center">
                          -
                        </td>
                        <td className="px-3 py-2 border text-xs text-gray-600 text-center">
                          -
                        </td>
                        <td className="px-3 py-2 border text-xs text-gray-600 text-center">
                          -
                        </td>
                        <td className="px-3 py-2 border text-xs text-gray-600 text-center">
                          -
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2 border text-xs text-gray-600 text-center">
                          -
                        </td>
                        <td className="px-3 py-2 border text-xs text-gray-600 text-center">
                          -
                        </td>
                      </>
                    )}
                    <td className="px-3 py-2 border">
                      <select
                        value={adj.employee_movement_id || ""}
                        onChange={(e) =>
                          handleAdjustmentChange(
                            adj.id,
                            "employee_movement_id",
                            e.target.value
                          )
                        }
                        className="w-full px-2 py-1 text-xs border-0 focus:ring-1 focus:ring-mila_orange rounded-sm"
                      >
                        <option value="">Select type...</option>
                        {movementOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 border">
                      <input
                        type="text"
                        value={adj.description}
                        onChange={(e) =>
                          handleAdjustmentChange(
                            adj.id,
                            "description",
                            e.target.value
                          )
                        }
                        placeholder="Adjustment reason..."
                        className="w-full px-2 py-1 text-xs border-0 focus:ring-1 focus:ring-mila_orange rounded-sm"
                      />
                    </td>
                    <td className="px-3 py-2 text-center border">
                      <input
                        type="number"
                        value={adj.hours}
                        onChange={(e) =>
                          handleAdjustmentChange(adj.id, "hours", e.target.value)
                        }
                        placeholder="0"
                        step="0.5"
                        className="w-full px-2 py-1 text-xs border border-gray-300 focus:ring-1 focus:ring-mila_orange rounded-sm text-center font-bold"
                      />
                    </td>
                    <td className="px-3 py-2 text-center border">
                      <button
                        type="button"
                        onClick={() => handleDeleteAdjustment(adj.id)}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors"
                        title="Delete adjustment"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}

                {entries.map((entry, idx) => {
                  const { weekday, day } = formatDate(entry.date);
                  return (
                    <tr key={entry.id} className="bg-white hover:bg-gray-50">
                      <td className="px-3 py-2 text-center border">
                        <span className="text-xs font-semibold text-gray-600 uppercase">
                          {weekday}
                        </span>
                      </td>
                      <td className="px-3 py-2 border">
                        <input
                          type="date"
                          value={entry.date}
                          onChange={(e) => handleFieldChange(entry.id, "date", e.target.value)}
                          className="w-full px-2 py-1 text-xs border-0 focus:ring-1 focus:ring-mila_orange rounded-sm"
                        />
                      </td>
                      {isFaculty ? (
                        <>
                          <td className="px-3 py-2 text-center border">
                            <input
                              type="time"
                              value={entry.morning_from}
                              onChange={(e) =>
                                handleFieldChange(entry.id, "morning_from", e.target.value)
                              }
                              className="w-full px-2 py-1 text-xs border-0 focus:ring-1 focus:ring-mila_orange rounded-sm text-center"
                            />
                          </td>
                          <td className="px-3 py-2 text-center border">
                            <input
                              type="time"
                              value={entry.morning_to}
                              onChange={(e) =>
                                handleFieldChange(entry.id, "morning_to", e.target.value)
                              }
                              className="w-full px-2 py-1 text-xs border-0 focus:ring-1 focus:ring-mila_orange rounded-sm text-center"
                            />
                          </td>
                          <td className="px-3 py-2 text-center border">
                            <input
                              type="time"
                              value={entry.afternoon_from}
                              onChange={(e) =>
                                handleFieldChange(entry.id, "afternoon_from", e.target.value)
                              }
                              className="w-full px-2 py-1 text-xs border-0 focus:ring-1 focus:ring-mila_orange rounded-sm text-center"
                            />
                          </td>
                          <td className="px-3 py-2 text-center border">
                            <input
                              type="time"
                              value={entry.afternoon_to}
                              onChange={(e) =>
                                handleFieldChange(entry.id, "afternoon_to", e.target.value)
                              }
                              className="w-full px-2 py-1 text-xs border-0 focus:ring-1 focus:ring-mila_orange rounded-sm text-center"
                            />
                          </td>
                          <td className="px-3 py-2 text-center border">
                            <input
                              type="time"
                              value={entry.evening_from}
                              onChange={(e) =>
                                handleFieldChange(entry.id, "evening_from", e.target.value)
                              }
                              className="w-full px-2 py-1 text-xs border-0 focus:ring-1 focus:ring-mila_orange rounded-sm text-center"
                            />
                          </td>
                          <td className="px-3 py-2 text-center border">
                            <input
                              type="time"
                              value={entry.evening_to}
                              onChange={(e) =>
                                handleFieldChange(entry.id, "evening_to", e.target.value)
                              }
                              className="w-full px-2 py-1 text-xs border-0 focus:ring-1 focus:ring-mila_orange rounded-sm text-center"
                            />
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-2 text-center border">
                            <input
                              type="time"
                              value={entry.from}
                              onChange={(e) => handleFieldChange(entry.id, "from", e.target.value)}
                              className="w-full px-2 py-1 text-xs border-0 focus:ring-1 focus:ring-mila_orange rounded-sm text-center"
                            />
                          </td>
                          <td className="px-3 py-2 text-center border">
                            <input
                              type="time"
                              value={entry.to}
                              onChange={(e) => handleFieldChange(entry.id, "to", e.target.value)}
                              className="w-full px-2 py-1 text-xs border-0 focus:ring-1 focus:ring-mila_orange rounded-sm text-center"
                            />
                          </td>
                        </>
                      )}
                      <td className="px-3 py-2 border">
                        <select
                          value={entry.employee_movement_id || ""}
                          onChange={(e) =>
                            handleFieldChange(entry.id, "employee_movement_id", e.target.value)
                          }
                          className="w-full px-2 py-1 text-xs border-0 focus:ring-1 focus:ring-mila_orange rounded-sm"
                        >
                          <option value="">Select type...</option>
                          {movementOptions.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 border">
                        <input
                          type="text"
                          value={entry.description}
                          onChange={(e) => handleFieldChange(entry.id, "description", e.target.value)}
                          placeholder="Work description..."
                          className="w-full px-2 py-1 text-xs border-0 focus:ring-1 focus:ring-mila_orange rounded-sm"
                        />
                      </td>
                      <td className="px-3 py-2 text-center border">
                        <span className="text-xs font-bold text-mila_orange">
                          {entry.hours > 0 ? entry.hours.toFixed(1) : "-"}h
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center border">
                        <button
                          type="button"
                          onClick={() => handleDeleteEntry(entry.id)}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors"
                          title="Delete entry"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-100">
                <tr>
                  <td
                    colSpan={isFaculty ? 10 : 6}
                    className="px-3 py-2 text-xs font-bold text-right border"
                  >
                    Total:
                  </td>
                  <td className="px-3 py-2 text-center border">
                    <span className="text-xs font-bold text-sky-700">{totalHours.toFixed(1)}h</span>
                  </td>
                  <td className="px-3 py-2 border"></td>
                </tr>
              </tfoot>
            </table>
        </div>

      </div>

      )}

      {showConflictModal && conflictInfo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,.75)" }}
        >
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-start justify-between p-4 border-b">
              <div className="pr-4">
                <h3 className="text-lg font-bold text-gray-800">Conflicting timesheets detected</h3>
                {conflictInfo?.allowOverride ? (
                  <p className="text-sm text-gray-600 mt-1">
                    There are existing timesheets overlapping the selected period. Review and confirm to override.
                  </p>
                ) : (
                  <p className="text-sm text-red-600 mt-1">
                    Cannot override submitted/approved/rejected timesheets. Please resolve these conflicts before creating a new one.
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowConflictModal(false);
                  setConflictInfo(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 flex-1 overflow-auto space-y-3">
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-700">
                <p>
                  <span className="font-semibold">Staff:</span>{" "}
                  {selectedStaffName || selectedStaffId || "-"}
                </p>
                <p className="mt-1">
                  <span className="font-semibold">Period:</span>{" "}
                  <span className="font-mono">{periodFrom}</span> to{" "}
                  <span className="font-mono">{periodTo}</span>
                </p>
              </div>

              <div className="border border-orange-200 rounded-md overflow-hidden">
                <div className="bg-orange-50 px-3 py-2 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-orange-800">
                      Conflicts ({conflictInfo?.conflicts?.length || 0})
                    </p>
                    <p className="text-xs text-gray-700">
                      Existing timesheets overlap the selected period.
                    </p>
                  </div>
                  <span className="text-[10px] uppercase font-bold text-orange-700 bg-white border border-orange-200 rounded-full px-2 py-1">
                    Review
                  </span>
                </div>
                <div className="divide-y divide-orange-100">
                  {conflictInfo?.conflicts?.map((conflict) => (
                    <div key={conflict.id} className="px-3 py-2 flex items-center justify-between text-sm">
                      <div>
                        <p className="font-semibold text-gray-800">Timesheet</p>
                        <p className="text-xs text-gray-700">
                          {conflict.period_from} — {conflict.period_to}
                        </p>
                      </div>
                      <span className="text-xs font-semibold px-2 py-1 rounded-full bg-orange-100 text-orange-800 uppercase border border-orange-200">
                        {conflict.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {conflictInfo?.newTimesheetStatusOnOverride && (
                <div className="text-xs text-gray-700 bg-blue-50 border border-blue-200 rounded-md p-3">
                  On override, conflicting timesheets will be marked as{" "}
                  <span className="font-semibold">{conflictInfo.overrideStatus || "override"}</span>{" "}
                  and new ones will be created as{" "}
                  <span className="font-semibold">{conflictInfo.newTimesheetStatusOnOverride}</span>.
                </div>
              )}
            </div>

            <div className="p-4 border-t flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowConflictModal(false);
                  setConflictInfo(null);
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmOverride}
                disabled={!conflictInfo?.allowOverride || isSaving}
                className="px-4 py-2 bg-mila_orange hover:bg-orange-600 text-white font-semibold rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "Overriding..." : "Override and create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isStaffModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,.75)" }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <div className="flex-1">
                <h2 className="text-xl font-bold">Select Staff Member</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Choose a staff member to create or manage their timesheet
                </p>
              </div>
              <button
                onClick={() => {
                  setIsStaffModalOpen(false);
                  setSearchTerm("");
                  setFilterEmployeeType("");
                  setFilterDepartment("");
                  setPage(1);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-4 border-b bg-gray-50 flex-shrink-0">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <span>
                    Showing <span className="font-semibold">{filteredStaffs.length}</span> of <span className="font-semibold">{staffs.length}</span> staff members
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by name..."
                      className="w-72 pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-500"
                    />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  </div>
                  <select
                    value={filterEmployeeType}
                    onChange={(e) => setFilterEmployeeType(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">All Types</option>
                    <option value="Faculty">Faculty</option>
                    <option value="Staff">Staff</option>
                  </select>
                  <input
                    type="text"
                    value={filterDepartment}
                    onChange={(e) => setFilterDepartment(e.target.value)}
                    placeholder="Department..."
                    className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-auto min-h-0">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 bg-gray-100 z-10 shadow-sm">
                  <tr className="border-b-2 border-gray-300">
                    <th
                      className="text-left p-3 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center gap-2">
                        Name
                        <ArrowUpDown size={14} className={sortBy === "name" ? "text-orange-500" : "text-gray-400"} />
                      </div>
                    </th>
                    <th
                      className="text-left p-3 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors"
                      onClick={() => handleSort("employee_type")}
                    >
                      <div className="flex items-center gap-2">
                        Employee Type
                        <ArrowUpDown size={14} className={sortBy === "employee_type" ? "text-orange-500" : "text-gray-400"} />
                      </div>
                    </th>
                    <th
                      className="text-left p-3 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors"
                      onClick={() => handleSort("department")}
                    >
                      <div className="flex items-center gap-2">
                        Department
                        <ArrowUpDown size={14} className={sortBy === "department" ? "text-orange-500" : "text-gray-400"} />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pageStaffs?.map((staff, idx) => (
                    <tr
                      key={staff.id}
                      onClick={() => handleSelectStaff(staff)}
                      className={`border-b transition-colors cursor-pointer ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-orange-50`}
                    >
                      <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{staff.name}</td>
                      <td className="p-3 text-sm text-gray-700 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          staff.employee_type === "Faculty"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-green-100 text-green-700"
                        }`}>
                          {staff.employee_type || "N/A"}
                        </span>
                      </td>
                      <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{staff.department?.name || "-"}</td>
                    </tr>
                  ))}
                  {(!pageStaffs || pageStaffs.length === 0) && (
                    <tr>
                      <td colSpan="3" className="p-4 text-center text-gray-500 text-sm">
                        {debouncedTerm || filterEmployeeType || filterDepartment
                          ? "No staff members found matching the filters"
                          : "No staff members available"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t flex items-center justify-between bg-gray-50 text-sm flex-shrink-0">
              <div className="text-sm text-gray-600">Page {page} of {totalPages}</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="text-sm px-3 font-medium text-gray-700">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
